/**
 * Admin User Stats Component
 *
 * Statistics cards for admin user dashboard.
 *
 * @fileoverview Admin user statistics display component
 */

import React from 'react';
import {
  SimpleGrid,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import {
  MdPeople,
  MdPersonAdd,
  MdAdminPanelSettings,
  MdTrendingUp,
} from 'react-icons/md';
import type { UserStats } from '../../types/admin';

interface AdminUserStatsProps {
  /** User statistics data */
  stats: UserStats | null;
  /** Loading state */
  loading: boolean;
}

interface StatCardProps {
  /** Stat label */
  label: string;
  /** Stat value */
  value: number;
  /** Help text */
  helpText: string;
  /** Icon component */
  icon: React.ElementType;
  /** Icon color */
  iconColor: string;
}

/**
 * Individual stat card component
 *
 * @param props - Component props
 * @returns React component
 */
const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  helpText,
  icon,
  iconColor,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      bg={bgColor}
      p={5}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      position="relative"
      overflow="hidden"
    >
      <Icon
        as={icon}
        position="absolute"
        right={4}
        top={4}
        boxSize={10}
        color={iconColor}
        opacity={0.3}
      />
      <Stat>
        <StatLabel color="gray.500" fontSize="sm">
          {label}
        </StatLabel>
        <StatNumber fontSize="3xl" fontWeight="bold">
          {value.toLocaleString()}
        </StatNumber>
        <StatHelpText fontSize="xs" mb={0}>
          {helpText}
        </StatHelpText>
      </Stat>
    </Box>
  );
};

/**
 * Loading skeleton for stats
 *
 * @returns React component
 */
const StatCardSkeleton: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      bg={bgColor}
      p={5}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
    >
      <Skeleton h="16px" w="80px" mb={2} />
      <Skeleton h="36px" w="60px" mb={2} />
      <Skeleton h="12px" w="100px" />
    </Box>
  );
};

/**
 * Admin User Stats Component
 *
 * Displays user statistics in cards.
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <AdminUserStats stats={stats} loading={loading} />
 */
export const AdminUserStats: React.FC<AdminUserStatsProps> = ({
  stats,
  loading,
}) => {
  if (loading || !stats) {
    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
      <StatCard
        label="Total Users"
        value={stats.total}
        helpText="All registered users"
        icon={MdPeople}
        iconColor="blue.500"
      />
      <StatCard
        label="Active Users"
        value={stats.active}
        helpText="Currently active"
        icon={MdPersonAdd}
        iconColor="green.500"
      />
      <StatCard
        label="Administrators"
        value={stats.admins}
        helpText="Global admin role"
        icon={MdAdminPanelSettings}
        iconColor="purple.500"
      />
      <StatCard
        label="Recent Signups"
        value={stats.recentSignups}
        helpText="Last 7 days"
        icon={MdTrendingUp}
        iconColor="orange.500"
      />
    </SimpleGrid>
  );
};

export default AdminUserStats;
